import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Space } from "antd"
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons"
import { useQuery } from "@tanstack/react-query"
import type React from "react"
import { AI_ACTION_TYPES } from "@/constants/ai"
import { aiLogService, type AiLogQueryParams } from "@/services/log/aiLogApi"
import dayjs from "dayjs"

const { RangePicker } = DatePicker

interface AiFilterFormProps {
  onSearch: (values: AiLogQueryParams) => void
  loading?: boolean
  initialValues?: AiLogQueryParams
}

export const AiFilterForm: React.FC<AiFilterFormProps> = ({
  onSearch,
  loading = false,
  initialValues,
}) => {
  const [form] = Form.useForm()

  const { data: modelsResult } = useQuery({
    queryKey: ["ai_models_list"],
    queryFn: () => aiLogService.getModels(),
  })

  const modelOptions = (modelsResult?.data || []).map((m) => ({
    label: m,
    value: m,
  }))

  const actionTypeOptions = Object.values(AI_ACTION_TYPES).map((item) => ({
    label: `${item.title} (${item.code})`,
    value: item.code,
  }))

  const handleFinish = (values: any) => {
    const { dates, ...rest } = values
    const queryParams: AiLogQueryParams = {
      ...rest,
      start_date: dates && dates[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : undefined,
      end_date: dates && dates[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : undefined,
    }
    onSearch(queryParams)
  }

  const handleReset = () => {
    form.resetFields()
    onSearch({})
  }

  return (
    <Card className="shadow-xs mb-2">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          search: initialValues?.search,
          action_type: initialValues?.action_type,
          model: initialValues?.model,
          dates:
            initialValues?.start_date && initialValues?.end_date
              ? [dayjs(initialValues.start_date), dayjs(initialValues.end_date)]
              : undefined,
        }}
      >
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Người dùng (Tên, Email hoặc #ID)" name="search">
              <Input placeholder="Nhập tên, email hoặc #ID..." allowClear />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Tính năng AI" name="action_type">
              <Select
                placeholder="Tất cả tính năng"
                allowClear
                options={actionTypeOptions}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Model AI" name="model">
              <Select
                placeholder="Tất cả model"
                allowClear
                options={modelOptions}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Thời gian" name="dates">
              <RangePicker
                className="w-full"
                placeholder={["Từ ngày", "Đến ngày"]}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>

          <Col xs={24} className="flex justify-end">
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Đặt lại
              </Button>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
                Tìm kiếm
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>
  )
}

export default AiFilterForm
